// journal/update — the Journal's pure `update` (module contract) and its
// `inbound`. Model × Msg → Step (next model, effects as data) or an explicit
// refusal when an intent is not offered in the current state (no page, an
// unknown period, nothing to correct). Outcomes for a scale or period no
// longer shown are ignored (a no-op Step, not a refusal).

import { exhaustSwitchCase, match, Result } from "@bilda/utils";
import {
    type AssistantText,
    type JournalPage,
    type JournalPeriod,
    periodId,
    periodStart,
} from "../../app/ports/journal.js";
import type { Inbound, Step, Update } from "../module.js";
import type { Signal } from "../signal.js";
import type { JournalEffect } from "./effect.js";
import { type JournalModel, noProblem } from "./model.js";
import type { JournalMsg } from "./msg.js";
import { rowKey } from "./view-model.js";

export type JournalRefusal =
    | { readonly type: "journal_not_loaded" }
    | { readonly type: "period_unknown"; readonly periodId: string }
    | { readonly type: "nothing_to_correct"; readonly periodId: string }
    | { readonly type: "no_correction_open" }
    | { readonly type: "nothing_to_unlock"; readonly periodId: string }
    | { readonly type: "already_summarized"; readonly periodId: string }
    | { readonly type: "nothing_older" }
    | { readonly type: "erase_all_not_asked" };

type JournalStep = Step<JournalModel, JournalEffect>;
type Outcome = Result.Result<JournalStep, JournalRefusal>;

const step = (
    model: JournalModel,
    effects: readonly JournalEffect[] = [],
): Outcome => Result.ok({ model, effects, signals: [] });
const refuse = (refusal: JournalRefusal): Outcome => Result.err(refusal);

const put = <K, V>(m: ReadonlyMap<K, V>, k: K, v: V): ReadonlyMap<K, V> =>
    new Map(m).set(k, v);
const without = <K, V>(m: ReadonlyMap<K, V>, k: K): ReadonlyMap<K, V> => {
    const next = new Map(m);
    next.delete(k);
    return next;
};
const toggled = (s: ReadonlySet<string>, id: string): ReadonlySet<string> => {
    const next = new Set(s);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    return next;
};

const loadedPage = (model: JournalModel): JournalPage | undefined =>
    model.page._tag === "Page.Loaded" ? model.page.page : undefined;

const findPeriod = (
    model: JournalModel,
    id: string,
): JournalPeriod | undefined =>
    loadedPage(model)?.periods.find((p) => periodId(p.key) === id);

// Replace one period of the loaded page (no-op when not loaded).
const mapPeriod = (
    model: JournalModel,
    id: string,
    f: (p: JournalPeriod) => JournalPeriod,
): JournalModel => {
    const page = loadedPage(model);
    return page === undefined
        ? model
        : {
              ...model,
              page: {
                  _tag: "Page.Loaded",
                  page: {
                      ...page,
                      periods: page.periods.map((p) =>
                          periodId(p.key) === id ? f(p) : p,
                      ),
                  },
              },
          };
};

const mapPage = (
    model: JournalModel,
    f: (page: JournalPage) => JournalPage,
): JournalModel => {
    const page = loadedPage(model);
    return page === undefined
        ? model
        : { ...model, page: { _tag: "Page.Loaded", page: f(page) } };
};

// Commit an own-text draft: write it (or erase, when emptied) unless it is
// what the server already holds.
type Commit = {
    readonly model: JournalModel;
    readonly effects: readonly JournalEffect[];
};
const commitOwn = (
    model: JournalModel,
    period: JournalPeriod,
    text: string,
): Commit => {
    const id = periodId(period.key);
    return text === period.own
        ? {
              model: { ...model, drafts: without(model.drafts, id) },
              effects: [{ type: "drop_stash", periodId: id }],
          }
        : {
              model: {
                  ...model,
                  drafts: put(model.drafts, id, { text, phase: "saving" }),
              },
              effects: [
                  text === ""
                      ? { type: "erase_own", key: period.key }
                      : { type: "write_own", key: period.key, text },
              ],
          };
};

const loading = (model: JournalModel): JournalModel => ({
    ...model,
    page: { _tag: "Page.Loading" },
    older: "idle",
    problem: noProblem,
});

// Read the page again at the model's scale and reach.
const reload = (model: JournalModel): JournalEffect => ({
    type: "load_page",
    scale: model.scale,
    from: model.from,
});

// The AI's text a correction keeps: the summary itself, or what an earlier
// correction kept (the server's fold, mirrored for the optimistic write).
const originalOf = (a: AssistantText | null): string | null => {
    if (a === null) return null;
    switch (a.type) {
        case "written":
            return a.markdown;
        case "corrected":
        case "unlocked":
            return a.original;
        default:
            return exhaustSwitchCase(a);
    }
};

// Optimistic assistant write: apply `next`, remember what it replaced.
const writeAssistant = (
    model: JournalModel,
    period: JournalPeriod,
    next: AssistantText,
): JournalModel => {
    const id = periodId(period.key);
    return {
        ...mapPeriod(model, id, (p) => ({ ...p, assistant: next })),
        rollback: model.rollback.has(id)
            ? model.rollback
            : put(model.rollback, id, period.assistant),
        problem: noProblem,
    };
};

export const updateJournal: Update<
    JournalModel,
    JournalMsg,
    JournalEffect,
    JournalRefusal
> = (model, msg) => {
    switch (msg.type) {
        case "journal_opened":
        case "reload_requested":
            return step(loading(model), [reload(model)]);
        case "scale_chosen": {
            const next: JournalModel = {
                ...loading(model),
                scale: msg.scale,
                from: null,
                open: new Set(),
                reveal: null,
            };
            return step(next, [reload(next)]);
        }
        case "child_opened": {
            // The page reaches back to the child, which opens in view.
            const id = periodId(msg.key);
            const next: JournalModel = {
                ...loading(model),
                scale: msg.key.scale,
                from: periodStart(msg.key),
                open: new Set([id]),
                reveal: id,
            };
            return step(next, [reload(next)]);
        }
        case "older_requested": {
            const olderFrom = loadedPage(model)?.olderFrom ?? null;
            if (olderFrom === null) return refuse({ type: "nothing_older" });
            if (model.older === "loading") return step(model);
            const next: JournalModel = {
                ...model,
                from: olderFrom,
                older: "loading",
            };
            return step(next, [reload(next)]);
        }
        case "period_toggled":
            return findPeriod(model, msg.periodId) === undefined
                ? refuse({ type: "period_unknown", periodId: msg.periodId })
                : step({ ...model, open: toggled(model.open, msg.periodId) });
        case "original_toggled":
            return step({
                ...model,
                originals: toggled(model.originals, msg.periodId),
            });
        case "voice_chosen":
            return step({
                ...model,
                voices: put(model.voices, msg.periodId, msg.voice),
            });
        case "write_invited":
            return findPeriod(model, msg.periodId) === undefined
                ? refuse({ type: "period_unknown", periodId: msg.periodId })
                : step({
                      ...model,
                      open: new Set(model.open).add(msg.periodId),
                      voices: put(model.voices, msg.periodId, "own"),
                  });
        case "row_selected": {
            const key = rowKey(msg.activity);
            if (model.selectedRows.get(msg.periodId) === key)
                return step({
                    ...model,
                    selectedRows: without(model.selectedRows, msg.periodId),
                });
            const id = msg.activity.id;
            const known = model.histories.get(id);
            const read = known === undefined || known.type === "failed";
            return step(
                {
                    ...model,
                    selectedRows: put(model.selectedRows, msg.periodId, key),
                    histories: read
                        ? put(model.histories, id, { type: "loading" })
                        : model.histories,
                },
                read ? [{ type: "load_history", resourceId: id }] : [],
            );
        }
        case "own_edited":
            return findPeriod(model, msg.periodId) === undefined
                ? refuse({ type: "period_unknown", periodId: msg.periodId })
                : step(
                      {
                          ...model,
                          drafts: put(model.drafts, msg.periodId, {
                              text: msg.text,
                              phase: "editing",
                          }),
                      },
                      [
                          {
                              type: "stash_draft",
                              periodId: msg.periodId,
                              text: msg.text,
                          },
                      ],
                  );
        case "own_committed": {
            const period = findPeriod(model, msg.periodId);
            const draft = model.drafts.get(msg.periodId);
            if (period === undefined)
                return refuse({
                    type: "period_unknown",
                    periodId: msg.periodId,
                });
            // Nothing typed, or already on its way: nothing to commit.
            if (draft === undefined || draft.phase === "saving")
                return step(model);
            const commit = commitOwn(model, period, draft.text);
            return step(commit.model, commit.effects);
        }
        case "correction_started": {
            const period = findPeriod(model, msg.periodId);
            if (period === undefined)
                return refuse({
                    type: "period_unknown",
                    periodId: msg.periodId,
                });
            return period.assistant === null
                ? refuse({ type: "nothing_to_correct", periodId: msg.periodId })
                : step({
                      ...model,
                      correction: {
                          periodId: msg.periodId,
                          draft: period.assistant.markdown,
                      },
                      problem: noProblem,
                  });
        }
        case "correction_drafted":
            return model.correction === null
                ? refuse({ type: "no_correction_open" })
                : step({
                      ...model,
                      correction: { ...model.correction, draft: msg.draft },
                  });
        case "correction_cancelled":
            return step({ ...model, correction: null });
        case "correction_saved": {
            const { correction } = model;
            if (correction === null)
                return refuse({ type: "no_correction_open" });
            const period = findPeriod(model, correction.periodId);
            if (period === undefined)
                return refuse({
                    type: "period_unknown",
                    periodId: correction.periodId,
                });
            return step(
                {
                    ...writeAssistant(model, period, {
                        type: "corrected",
                        markdown: correction.draft,
                        original: originalOf(period.assistant),
                        correctedAt: msg.at,
                    }),
                    correction: null,
                },
                [
                    {
                        type: "correct_assistant",
                        key: period.key,
                        markdown: correction.draft,
                    },
                ],
            );
        }
        case "unlock_requested": {
            const period = findPeriod(model, msg.periodId);
            if (period === undefined)
                return refuse({
                    type: "period_unknown",
                    periodId: msg.periodId,
                });
            const { assistant } = period;
            return assistant?.type !== "corrected"
                ? refuse({ type: "nothing_to_unlock", periodId: msg.periodId })
                : step(
                      writeAssistant(model, period, {
                          ...assistant,
                          type: "unlocked",
                      }),
                      [{ type: "unlock_assistant", key: period.key }],
                  );
        }
        case "reading_allowed":
        case "reading_disallowed": {
            const allow = msg.type === "reading_allowed";
            return loadedPage(model) === undefined
                ? refuse({ type: "journal_not_loaded" })
                : step(
                      {
                          ...mapPage(model, (page) => ({
                              ...page,
                              assistantReadsOwn: allow,
                          })),
                          problem: noProblem,
                      },
                      [{ type: allow ? "allow_reading" : "disallow_reading" }],
                  );
        }
        case "page_loaded": {
            if (msg.page.scale !== model.scale) return step(model);
            const reveal = msg.page.periods.some(
                (p) => periodId(p.key) === model.reveal,
            )
                ? model.reveal
                : null;
            const loaded: JournalModel = {
                ...model,
                page: { _tag: "Page.Loaded", page: msg.page },
                older: "idle",
                reveal: null,
                rollback: new Map(),
            };
            // A draft left unsent on this device (a reload mid-sentence) is
            // committed now, unless the server already holds it.
            const commit = msg.page.periods.reduce<Commit>(
                (acc, period) => {
                    const text = msg.stashed.get(periodId(period.key));
                    if (text === undefined) return acc;
                    const next = commitOwn(acc.model, period, text);
                    return {
                        model: next.model,
                        effects: [...acc.effects, ...next.effects],
                    };
                },
                {
                    model: loaded,
                    effects:
                        reveal === null
                            ? []
                            : [{ type: "reveal_period", periodId: reveal }],
                },
            );
            return step(commit.model, commit.effects);
        }
        case "page_failed":
            return msg.scale !== model.scale
                ? step(model)
                : step({
                      ...model,
                      page: { _tag: "Page.Failed" },
                      older: "idle",
                  });
        case "own_written": {
            const draft = model.drafts.get(msg.periodId);
            const settled = mapPeriod(model, msg.periodId, (p) => ({
                ...p,
                own: msg.text,
            }));
            // Typed on while it saved: the newer draft stays.
            return draft !== undefined && draft.text !== msg.text
                ? step(settled)
                : step(
                      {
                          ...settled,
                          drafts: without(model.drafts, msg.periodId),
                      },
                      [{ type: "drop_stash", periodId: msg.periodId }],
                  );
        }
        case "own_write_failed": {
            const draft = model.drafts.get(msg.periodId);
            return draft === undefined || draft.text !== msg.text
                ? step(model)
                : step({
                      ...model,
                      drafts: put(model.drafts, msg.periodId, {
                          ...draft,
                          phase: "failed",
                      }),
                  });
        }
        case "assistant_write_answered":
            return step({
                ...model,
                rollback: without(model.rollback, msg.periodId),
            });
        case "assistant_write_failed": {
            if (!model.rollback.has(msg.periodId)) return step(model);
            const before = model.rollback.get(msg.periodId) ?? null;
            return step({
                ...mapPeriod(model, msg.periodId, (p) => ({
                    ...p,
                    assistant: before,
                })),
                rollback: without(model.rollback, msg.periodId),
                problem: {
                    _tag: "Problem.WriteFailed",
                    write: msg.write,
                    periodId: msg.periodId,
                },
            });
        }
        case "consent_answered":
            return step(model);
        case "summarize_requested": {
            const period = findPeriod(model, msg.periodId);
            if (period === undefined)
                return refuse({
                    type: "period_unknown",
                    periodId: msg.periodId,
                });
            if (period.assistant !== null)
                return refuse({
                    type: "already_summarized",
                    periodId: msg.periodId,
                });
            return model.summarizing.has(msg.periodId)
                ? step(model)
                : step(
                      {
                          ...model,
                          summarizing: new Set(model.summarizing).add(
                              msg.periodId,
                          ),
                          problem: noProblem,
                      },
                      [{ type: "summarize", key: period.key }],
                  );
        }
        case "summarized":
        case "summarize_failed": {
            const summarizing = new Set(model.summarizing);
            summarizing.delete(msg.periodId);
            // A new summary is read back with the page.
            return msg.type === "summarized"
                ? step({ ...model, summarizing }, [reload(model)])
                : step({
                      ...model,
                      summarizing,
                      problem: {
                          _tag: "Problem.WriteFailed",
                          write: "summarize",
                          periodId: msg.periodId,
                      },
                  });
        }
        case "erase_all_asked":
            return step({
                ...model,
                eraseAll: "confirming",
                problem: noProblem,
            });
        case "erase_all_cancelled":
            return step({ ...model, eraseAll: "idle" });
        case "erase_all_confirmed":
            return model.eraseAll !== "confirming"
                ? refuse({ type: "erase_all_not_asked" })
                : step(
                      {
                          ...mapPage(model, (page) => ({
                              ...page,
                              periods: page.periods.map((p) => ({
                                  ...p,
                                  own: "",
                              })),
                          })),
                          drafts: new Map(),
                          eraseAll: "erasing",
                      },
                      [
                          ...[...model.drafts.keys()].map(
                              (periodId): JournalEffect => ({
                                  type: "drop_stash",
                                  periodId,
                              }),
                          ),
                          { type: "erase_all" },
                      ],
                  );
        case "all_erased":
        case "erase_all_failed":
            // Either way the page is read again: it says what is left.
            return step(
                {
                    ...model,
                    eraseAll: "idle",
                    problem:
                        msg.type === "erase_all_failed"
                            ? {
                                  _tag: "Problem.WriteFailed",
                                  write: "erase_all",
                                  periodId: null,
                              }
                            : model.problem,
                },
                [reload(model)],
            );
        case "history_loaded":
            return step({
                ...model,
                histories: put(model.histories, msg.resourceId, {
                    type: "loaded",
                    moments: msg.moments,
                }),
            });
        case "history_failed":
            return step({
                ...model,
                histories: put(model.histories, msg.resourceId, {
                    type: "failed",
                }),
            });
        case "consent_failed":
            return step({
                ...mapPage(model, (page) => ({
                    ...page,
                    assistantReadsOwn: msg.before,
                })),
                problem: {
                    _tag: "Problem.WriteFailed",
                    write: "consent",
                    periodId: null,
                },
            });
        default:
            return exhaustSwitchCase(msg);
    }
};

// The Journal listens to no Signal yet: it reads afresh each time it opens.
export const inboundJournal: Inbound<JournalMsg> = match<
    Signal,
    JournalMsg | undefined
>({
    "Signal.NoticeRaised": () => undefined,
    "Signal.FrameRequested": () => undefined,
    "Signal.WorldChanged": () => undefined,
    "Signal.ConversationStarted": () => undefined,
    "Signal.PendingGroundingFlushed": () => undefined,
    "Signal.ProposalLanded": () => undefined,
    "Signal.StopRequested": () => undefined,
});
